import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useMemo, useRef } from "react";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as yup from "yup";

import ERRORS from "common/errors";
import stringEncryptRSA from "common/string-encrypt-rsa";
import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";
import AppAlert from "components/app-alert";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import activateIcon from "assets/images/activate-icon.svg";
import exclamationIcon from "assets/images/exclamation-icon.svg";
import AppOtpModal from "components/app-otp-modal";
import AppSuccessfulModal from "./app-successful-modal";

export const AppUpdatePasswordModal = (props, ref) => {
	const [visible, setVisible] = useState(false);
	const otpModalRef = useRef();
	const updateSuccessRef = useRef();
	const formik = useFormik({
		initialValues: {
			newPassword: "",
			currentPassword: "",
			confirmPassword: "",
		},
		validationSchema: yup.object({
			newPassword: yup.string().required(ERRORS.REQUIRED).test("no-space", ERRORS.SPACE, (value) => !/\s/.test(value || "")),
			currentPassword: yup.string().required(ERRORS.REQUIRED).test("no-space", ERRORS.SPACE, (value) => !/\s/.test(value || "")),
			confirmPassword: yup.string().required(ERRORS.REQUIRED).test("no-space", ERRORS.SPACE, (value) => !/\s/.test(value || "")),
		}),
		onSubmit: (values) => {
			onHandleSendOtp(values);
		},
	});

	const onHandleShow = useCallback(() => {
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		formik.resetForm();
		setVisible(false);
	}, [formik]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	const rules = useMemo(() => [
		{
			label: "Use at least 8 characters",
			isValid: formik.values.newPassword.length >= 8,
		},
		{
			label: "Should contain at least 1 uppercase",
			isValid: /[A-Z]/.test(formik.values.newPassword),
		},
		{
			label: "Should contain at least 1 numeric",
			isValid: /[0-9]/.test(formik.values.newPassword),
		},
		{
			label: "Should contain at least 1 special character",
			isValid: /[!@#$%^&*,.?]/.test(formik.values.newPassword),
		},
		{
			label: "Cannot be same as previous password",
			isValid: formik.values.newPassword !== formik.values.currentPassword,
		},
	], [formik.values.newPassword, formik.values.currentPassword]);

	const isFormValid = useMemo(() => {
		const allRulesPassed = rules.every(rule => rule.isValid);
		return allRulesPassed && formik.values.newPassword === formik.values.confirmPassword;
	}, [rules, formik.values.newPassword, formik.values.confirmPassword]);

	const onHandleSendOtp = async () => {
		try {
			await api.get.general.sendOtp();

			formik.setSubmitting(false);
			onHandleOtpModal();

		} catch (error) {
			serveRequestErrors(error, (message) => {
				formik.setFieldError("confirmPassword", message);
			});
		} finally {
			formik.setSubmitting(false);
		}
	}

	const onHandleOtpModal = useCallback(() => {
		otpModalRef.current.onHandleShow();
	}, []);

	const onHandleSuccessModal = useCallback(() => {
		updateSuccessRef.current.onHandleShow();
	}, []);

	const onHandleResetPassword = useCallback(async (otpCode) => {
		let values = { ...formik.values, otpCode };

		try {
			const payload = {
				currentPassword: stringEncryptRSA(values.currentPassword),
				newPassword: stringEncryptRSA(values.newPassword),
				otpCode: values.otpCode,
			};

			await api.post.general.updatePassword(payload);
			onHandleSuccessModal();

		} catch (error) {
			serveRequestErrors(error, (message) => {
				formik.setFieldError("currentPassword", message);
			});
		} finally {
			formik.setSubmitting(false);
		}
	}, [formik, onHandleSuccessModal]);

	return (
		<Modal classes={{ root: "app-update-password" }} open={visible} aria-labelledby="update-password" aria-describedby="update-password-modal">
			<div className="update-password">
				<AppAlert />
				{/* <img className="forgot-password__hero" src={hero} alt="hero" /> */}

				<div className="update-password__header">
					<p className="update-password__title">Change Password</p>
				</div>

				<div className="update-password__content">
					<form className="update-password__form" onSubmit={formik.handleSubmit}>

						{/* prettier-ignore */}
						<AppInput type="password" name="currentPassword" label="Current Password" placeholder="Enter Current Password" value={formik.values.currentPassword} error={formik.errors.currentPassword} touched={formik.touched.currentPassword} onChange={formik.handleChange} />

						<AppInput type="password" name="newPassword" label="New Password" placeholder="Enter New Password" value={formik.values.newPassword} error={formik.errors.newPassword} touched={formik.touched.newPassword} onChange={formik.handleChange} maxLength={20} />

						<ul className="update-password__rules">
							{rules.map((rule, idx) => (
								<li key={idx} className="update-password__rules__maps">
									{rule.isValid ? (
										<img src={activateIcon} alt="activateIcon" className="update-password__rules__maps__icon" />
									) : (
										<img src={exclamationIcon} alt="exclamationIcon" className="update-password__rules__maps__icon" />
									)}
									<span className={rule.isValid ? "update-password__rules__maps__text-green" : "update-password__rules__maps__text-gray"}>
										{rule.label}
									</span>
								</li>
							))}
						</ul>

						<AppInput type="password" name="confirmPassword" label="Confirm New Password" placeholder="Enter Confirm Password" value={formik.values.confirmPassword} error={formik.errors.confirmPassword} touched={formik.touched.confirmPassword} onChange={formik.handleChange} maxLength={20} />

						<div className="update-password__button-container">
							<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
							<AppButton type="submit" label="Change Password" disabled={!isFormValid || formik.isSubmitting} />
						</div>
					</form>
				</div>

				<AppOtpModal email={props.email} ref={otpModalRef} onSubmit={onHandleResetPassword} />
				<AppSuccessfulModal ref={updateSuccessRef} onClick={onHandleDismiss} />
			</div>
		</Modal>
		
	);
};

export default memo(forwardRef(AppUpdatePasswordModal));

AppUpdatePasswordModal.propTypes = {
	ref: PropTypes.object,
};
